import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { LoaderService } from '../../../common/services/loader.service';
import { ReportSelectorService } from '../../../form-controls/services/report-selector.service';
import { Subscription } from 'rxjs';
import { finalize, tap } from 'rxjs/operators';
import { TextTemplatesService } from '../../../text-templates/text-templates.service';
import { OnBaseDocumentGroup, OnBaseDocumentType, OnBaseKeyword, OnBaseKeywordMappingSearchSpec, OnBaseMappedKeyword, OnBaseProgramArea } from '../../../common/models/onBaseIntegrationAndMapping.model';
import { DocumentManagementService } from '../../../common/services/document-management.service';
import { OnBaseIntegrationAndMappingService } from '../../../common/services/onbase-integration-mapping.service';
import { Dialog, DialogButton } from '../../../core/models/dialog.model';
import { DynamicDialogService } from '../../../core/services/dynamic-dialog.service';

@Component({
    selector: 'onbase-configuration',
    templateUrl: './onbase-configuration.component.html',
    styleUrls: ['./onbase-configuration.component.scss'],
    standalone: false
})
export class OnBaseConfigurationComponent implements OnInit, OnDestroy {

    @Input() providedOnBaseDocumentGroupName: string;
    @Input() providedOnBaseDocumentTypeName: string;

    onBaseMappingService: DocumentManagementService

    @Output() selectedDocumentGroup: EventEmitter<OnBaseDocumentGroup> = new EventEmitter<OnBaseDocumentGroup>();
    @Output() selectedDocumentType: EventEmitter<OnBaseDocumentType> = new EventEmitter<OnBaseDocumentType>();
    @Output() relevantKeywords: EventEmitter<OnBaseMappedKeyword[]> = new EventEmitter<OnBaseMappedKeyword[]>();

    errorDialog: Dialog = new Dialog("An error has occurred", [new DialogButton("OK", false, () => { return; })]);

    subscription: Subscription = new Subscription();

    internalSelectedDocumentGroup: OnBaseDocumentGroup = { id: -1, name: "-- Select a Document Group --" };
    internalSelectedDocumentType: OnBaseDocumentType = { id: -1, name: "-- Select a Document Type --" };
    documentGroups: OnBaseDocumentGroup[] = [{ id: -1, name: "-- Select a Document Group --" }];
    documentTypes: OnBaseDocumentType[] = [{ id: -1, name: "-- Select a Document Type --" }];
    programAreas: OnBaseProgramArea[] = [];
    mappedKeyWords: OnBaseMappedKeyword[] = [];
    public docGroupToTypeDictionary: Map<string, OnBaseDocumentType[]> = new Map<string, OnBaseDocumentType[]>();
    public docTypeToMappedKeywordDictionary: Map<string, OnBaseMappedKeyword[]> = new Map<string, OnBaseMappedKeyword[]>();

    loadingDocTypes = false;
    loadingKeywordMappings = false;

    constructor(public loader: LoaderService, public _mappingService: OnBaseIntegrationAndMappingService, public reportService: ReportSelectorService,
        public textService: TextTemplatesService, private _dialogService: DynamicDialogService) { }

    ngOnInit() {
        let documentGroupsObs$ = this._mappingService.getAllDocumentGroups().pipe(tap((documentGroups: OnBaseDocumentGroup[]) => {
            this.documentGroups = this.documentGroups.concat(documentGroups);
            if (this.providedOnBaseDocumentGroupName) {
                this.internalSelectedDocumentGroup = this.documentGroups.filter(a => a.name == this.providedOnBaseDocumentGroupName)[0];
                this.selectedDocumentGroup.emit(this.internalSelectedDocumentGroup);
            }
        })).subscribe(() => { },
            (error) => {
                console.error(error);
                this.handleError(error, "An error occurred when trying to fetch the available Document Groups from OnBase.");
            }
        );
        this.subscription.add(documentGroupsObs$);

        if (this.providedOnBaseDocumentGroupName) {
            let documentGroupTypesObs$ = this._mappingService.getDocumentTypesByDocumentGroup(this.providedOnBaseDocumentGroupName).pipe(tap((documentTypes: OnBaseDocumentType[]) => {
                documentTypes.unshift({ id: -1, name: "-- Select a Document Type --" })
                this.docGroupToTypeDictionary.set(this.providedOnBaseDocumentGroupName, documentTypes);
                this.loadingDocTypes = false;
                if (this.providedOnBaseDocumentTypeName) {
                    this.internalSelectedDocumentType = documentTypes.filter(a => a.name == this.providedOnBaseDocumentTypeName)[0];
                    this.selectedDocumentType.emit(this.internalSelectedDocumentType);
                }
            })).subscribe(() => { },
                (error) => {
                    console.error(error);
                    this.handleError(error, "An error occurred when trying to fetch the related Document Types.");
                    this.loadingDocTypes = false;
                }
            );
            this.subscription.add(documentGroupTypesObs$);
        }

        if (this.providedOnBaseDocumentTypeName) {
            let documentTypeKeywordsObs$ = this._mappingService.GetKeywordsByDocumentType(this.providedOnBaseDocumentTypeName)
                .pipe(tap((mappedKeywords: OnBaseMappedKeyword[]) => {
                    //Strip out the file name one, as it will be set per file as they are uploaded within the DocumentManagementLogic Repository's CommitDocument method
                    this.mappedKeyWords = mappedKeywords.filter(a => a.name != "DOGRM - Legacy File Name");
                    this.docTypeToMappedKeywordDictionary.set(this.providedOnBaseDocumentTypeName, mappedKeywords);
                    this.loadingKeywordMappings = false;
                    this.relevantKeywords.emit(this.mappedKeyWords);
                })).subscribe(() => { },
                    (error) => {
                        console.error(error);
                        this.handleError(error, "An error occurred when trying to fetch the related Keywords.");
                        this.loadingKeywordMappings = false;
                    }
                );
            this.subscription.add(documentTypeKeywordsObs$);
        }
    }

    ngOnDestroy() {
        if (this.subscription) this.subscription.unsubscribe();
    }

    handleError(error: any, message: string): void {
        this.errorDialog.toggleDialog(true);
        this.errorDialog.message = message;
        this._dialogService.updateDialog(this.errorDialog);
    }

    docGroupSelected(event: any) {
        this.internalSelectedDocumentGroup = event;
        this.selectedDocumentGroup.emit(this.internalSelectedDocumentGroup);
        this.internalSelectedDocumentType = { id: -1, name: "-- Select a Document Type --" };
        this.selectedDocumentType.emit(this.internalSelectedDocumentType);
        this.documentTypes = [{ id: -1, name: "-- Select a Document Type --" }];

        if (this.internalSelectedDocumentGroup && this.internalSelectedDocumentGroup.name != "-- Select a Document Group --" && !this.docGroupToTypeDictionary.has(this.internalSelectedDocumentGroup.name)) {
            this.loadingDocTypes = true;
            let documentGroupTypesObs$ = this._mappingService.getDocumentTypesByDocumentGroup(this.internalSelectedDocumentGroup.name).pipe(tap((documentTypes: OnBaseDocumentType[]) => {
                documentTypes.unshift({ id: -1, name: "-- Select a Document Type --" })
                this.docGroupToTypeDictionary.set(this.internalSelectedDocumentGroup.name, documentTypes);
                this.loadingDocTypes = false;
            })).subscribe(() => { },
                (error) => {
                    console.error(error);
                    this.handleError(error, "An error occurred when trying to fetch the related Document Types.");
                    this.loadingDocTypes = false;
                }
            );
            this.subscription.add(documentGroupTypesObs$);
        }
    }

    docTypeSelected(event: any) {
        this.internalSelectedDocumentType = event;
        this.selectedDocumentType.emit(this.internalSelectedDocumentType);
        if (this.internalSelectedDocumentType && this.internalSelectedDocumentType.name != "-- Select a Document Type --" && !this.docTypeToMappedKeywordDictionary.has(this.internalSelectedDocumentGroup.name)) {
            this.loadingKeywordMappings = true;
            let documentTypeKeywordsObs$ = this._mappingService.GetKeywordsByDocumentGroup(this.internalSelectedDocumentGroup.name)
                .pipe(tap((mappedKeywords: OnBaseMappedKeyword[]) => {
                    //Strip out the file name one, as it will be set per file as they are uploaded within the DocumentManagementLogic Repository's CommitDocument method
                    this.mappedKeyWords = mappedKeywords.filter(a => a.name != "DOGRM - Legacy File Name");
                    this.docTypeToMappedKeywordDictionary.set(this.internalSelectedDocumentType.name, mappedKeywords);
                    this.relevantKeywords.emit(mappedKeywords);
                    this.loadingKeywordMappings = false;
                })).subscribe(() => { },
                    (error) => {
                        console.error(error);
                        this.handleError(error, "An error occurred when trying to fetch the related Keywords.");
                        this.loadingKeywordMappings = false;
                    }
                );
            this.subscription.add(documentTypeKeywordsObs$);
        }
        else if (this.selectedDocumentType && this.docTypeToMappedKeywordDictionary.has(this.internalSelectedDocumentType.name)) {
            this.mappedKeyWords = this.docTypeToMappedKeywordDictionary.get(this.internalSelectedDocumentGroup.name);
            this.relevantKeywords.emit(this.mappedKeyWords);
        }
    }

    validDocumentTypeSelected(): boolean {
        return this.selectedDocumentGroup && this.internalSelectedDocumentGroup.name != "-- Select a Document Group --"
            && this.selectedDocumentType && this.internalSelectedDocumentType.name != "-- Select a Document Type --";
    }

    filteredDocTypes() {
        //If a document group has been selected, filter down the available doc types
        if (this.selectedDocumentGroup && this.docGroupToTypeDictionary.has(this.internalSelectedDocumentGroup.name)) {
            this.documentTypes = this.docGroupToTypeDictionary.get(this.internalSelectedDocumentGroup.name);
        }

        return this.documentTypes;
    }
}